// Content model for the English-only Singapore (`/singapore/...`) country pages.
// Lean service-page shape; the shared governance / SGD / office / cross-link blocks
// are rendered by the template (SingaporeServicePage), not repeated per page.

export interface SingaporePageContent {
  route: string; // full route, e.g. "singapore" or "singapore/claude-implementation"
  eyebrow: string;
  h1: string;
  heroImage?: string; // defaults to the shared Singapore hero
  heroImageAlt?: string; // defaults to "<h1> — Anchor Sprint Singapore"
  intro: string;
  ctaLabel: string; // hero CTA button text (→ ctaHref, default /contact/)
  // Optional overrides for non-Claude SG pages (e.g. SonarQube). All default to
  // the Claude-page behaviour, so the hub and the four Claude pages are unchanged.
  ctaHref?: string; // hero + closing CTA target; default "/contact/"
  // Hero secondary link; default "See what we build" → agent use cases; null hides it.
  secondaryCta?: { label: string; href: string } | null;
  // Small link under the hero CTAs (e.g. "Buying for a Malaysian team?").
  heroCrossLink?: { intro: string; label: string; href: string };
  /** Optional offer link under the hero buttons (e.g. the free-website bundle). */
  heroOffer?: { label: string; href: string };
  // Claude Partner badge, PDPC/IMDA governance badge, agent/Claude cost-calculator
  // box and the Anthropic footer line. Default true.
  showClaudeChrome?: boolean;
  // Render the official Claude Partner Network Certified badge in the hero
  // (replaces the text credential chip on the hero image). One page only:
  // /singapore/claude-partner/. See ClaudePartnerCertifiedBadge for the rules.
  certifiedBadge?: boolean;
  valueHeading: string;
  values: { title: string; body: string }[]; // 3–4 outcome cards
  sections: {
    // Optional anchor so hubs and blogs can deep-link a section (e.g. #sast).
    id?: string;
    heading: string;
    body: string;
    bullets?: string[];
    // Optional editorial outbound links. Added so Singapore pages can point at
    // aideploy.sg from body copy — a sitewide footer link is worth roughly one
    // weak signal however many pages repeat it, which is why every aideploy inner
    // page still reads "Discovered - currently not indexed".
    links?: { href: string; label: string; intro?: string }[];
  }[];
  // Optional plan cards and numbered steps, rendered between the value cards and the
  // body sections. Added for /singapore/ai-seo/; every other SG page leaves them unset.
  plans?: {
    heading: string;
    intro: string;
    items: { name: string; price?: string; priceNote?: string; summary: string; features: string[] }[];
    note?: string;
  };
  steps?: { heading: string; items: { title: string; body: string }[] };
  faqs: { question: string; answer: string }[]; // renders FAQPage schema
  ctaTitle: string;
  ctaBody: string;
}

// Anchor Sprint's Singapore office (Suntec City). Single source of truth for the
// visible NAP block and the LocalBusiness schema.
export const SINGAPORE_OFFICE = {
  name: "Anchor Sprint Singapore",
  street: "7 Temasek Boulevard, #12-07 Suntec Tower One",
  city: "Singapore",
  postalCode: "038987",
  country: "SG",
  // Temporary local +65 line (swap to the permanent office line when assigned).
  phone: "+65 8749 0243",
  fax: "+65 6917 8977",
  legalName: "Anchor Sprint Pte. Ltd.",
  uen: "202632732Z",
  mapQuery: "7 Temasek Boulevard Suntec Tower One Singapore 038987",
} as const;

// The SG pages, in nav order (hub first). Used by the template's service sub-nav.
export const SINGAPORE_NAV: { route: string; label: string }[] = [
  { route: "singapore", label: "Singapore overview" },
  { route: "singapore/claude-implementation", label: "Claude implementation" },
  { route: "singapore/claude-partner", label: "Claude partner" },
  { route: "singapore/claude-training", label: "Claude training" },
  {
    route: "singapore/claude-managed-service",
    label: "Managed service",
  },
  { route: "singapore/sonarqube", label: "SonarQube" },
  { route: "singapore/ai-seo", label: "AI SEO" },
];
